import { load as loadYaml } from "js-yaml";

const PROMPT_KEYS = ["prompt0", "prompt1", "prompt2", "prompt3", "prompt4"];

let cachedConfig = null;

export function defaultGroundingTitle(date = new Date()) {
  return date.toLocaleDateString("da-DK", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export async function loadGroundingPrompts() {
  if (cachedConfig) return cachedConfig;
  const response = await fetch("/grounding-prompts.yaml");
  if (!response.ok) {
    throw new Error("Could not load grounding prompts.");
  }
  const text = await response.text();
  const parsed = loadYaml(text);
  if (!parsed || typeof parsed !== "object") {
    throw new Error("Invalid grounding prompts file.");
  }
  cachedConfig = parsed;
  return parsed;
}

export function getGroundingMusicSrc(config) {
  const src = String(config?.music || "/grounding-music.flac").trim();
  if (!src) return "/grounding-music.flac";
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  const path = src.startsWith("/") ? src : `/${src}`;
  return encodeURI(path);
}

export function getGroundingMusicStart(config) {
  const raw = config?.music_start ?? config?.musicStart ?? 0;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds < 0) return 0;
  return seconds;
}

export function getGroundingMusicSettings(config) {
  return {
    src: getGroundingMusicSrc(config),
    startSeconds: getGroundingMusicStart(config),
  };
}

function pickRandom(list) {
  if (!Array.isArray(list) || !list.length) return "";
  return list[Math.floor(Math.random() * list.length)];
}

function normalizeStepConfig(raw) {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const prompts = raw.prompts || raw.options || [];
    return {
      text: pickRandom(prompts),
      autoskip: Boolean(raw.autoskip),
      duration: Math.max(1, Number(raw.duration) || 10),
    };
  }
  if (Array.isArray(raw)) {
    return {
      text: pickRandom(raw),
      autoskip: false,
      duration: 60,
    };
  }
  return {
    text: "",
    autoskip: false,
    duration: 60,
  };
}

export function pickRandomPrompts(config) {
  return PROMPT_KEYS.map((key) => normalizeStepConfig(config?.[key])).filter(
    (step) => step.text
  );
}

export function buildGroundingBody(steps, answers) {
  const sections = steps.map((step, index) => {
    const prompt = typeof step === "string" ? step : step.text;
    const answer = String(answers[index] || "").trim();
    return `## ${prompt}\n\n${answer}`;
  });
  return sections.join("\n\n");
}

/** Parse stored grounding markdown into prompt/answer pairs. */
export function parseGroundingSections(body) {
  const raw = String(body || "").trim();
  if (!raw) return [];

  const parts = raw.split(/\n(?=##\s+)/);
  return parts
    .map((part) => {
      const trimmed = part.trim();
      const match = trimmed.match(/^##\s+([\s\S]*?)(?:\n\n([\s\S]*))?$/);
      if (!match) {
        return { prompt: trimmed, answer: "" };
      }
      return {
        prompt: match[1].trim(),
        answer: (match[2] || "").trim(),
      };
    })
    .filter((section) => section.prompt || section.answer);
}
